import { useState } from 'react';
import { useQuery } from 'react-query';

import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { PlusIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { logger } from '~/features/logging/logger';
import { getProgramReviews } from '~/features/program/controllers/get-program-reviews';
import type { ProgramReview, ProgramReviews } from '~/features/program/models/program-review';
import type { StaticReviewsSectionProps } from '~/features/program/utils/transform-static-reviews-section-props';

import { CAROUSEL_REVIEW_COUNT } from '../constants';
import { formatCountLabel } from '../utils/format-count-label';
import { getReviewsSectionTitle } from '../utils/get-reviews-section-title';
import { getTestimonials } from '../utils/get-testimonials';

import { GraduateTestimonialsSubsection } from './graduate-testimonials-subsection';
import { ReviewsModal } from './reviews-modal';
import { TrustpilotReviewsSubsection } from './trustpilot-reviews-subsection';

export type ReviewsTestimonialsSectionProps = {
  programKey: string;
  /**
   * Null for programs under `REVIEW_MINIMUM_THRESHOLD`, where a handful of
   * Trustpilot reviews would misrepresent the program. Graduate testimonials are
   * curated separately, so the section still renders without this.
   */
  staticReviewsSectionProps: StaticReviewsSectionProps;
};

const NO_REVIEWS: ProgramReview[] = [];

export function ReviewsTestimonialsSection({ programKey, staticReviewsSectionProps }: ReviewsTestimonialsSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasReviews = Boolean(staticReviewsSectionProps);
  const hasTestimonials = Boolean(getTestimonials(programKey)?.length);
  const initReviews = staticReviewsSectionProps?.initReviews ?? NO_REVIEWS;
  const numberOfReviews = staticReviewsSectionProps?.numberOfReviews ?? 0;
  const starsAverage = staticReviewsSectionProps?.starsAverage ?? 0;

  // The server payload only carries the first page of reviews, which is far
  // fewer than the carousel can scroll through. Widening it on the client keeps
  // the initial HTML (and its layout) intact while the longer list loads.
  const { data: carouselReviews = initReviews } = useQuery<ProgramReviews>(
    ['reviews-carousel', programKey],
    () => getProgramReviews(programKey, 1, CAROUSEL_REVIEW_COUNT),
    {
      initialData: initReviews,
      enabled: numberOfReviews > initReviews.length,
      onError: (error) => {
        logger.error('Failed to load additional reviews for the PLP carousel', { error, programKey });
      },
    }
  );

  function handleReadMoreClick() {
    trackEvent({
      name: 'Product Page CTA Clicked',
      type: 'click',
      label: 'reviews_read_more_clicked',
      additionalAttributes: { product_ids: [programKey] },
    });
    setIsModalOpen(true);
  }

  function handleReviewsArrowClick() {
    trackEvent({
      name: 'Product Page CTA Clicked',
      type: 'click',
      label: 'reviews_carousel_scrolled',
      additionalAttributes: { product_ids: [programKey] },
    });
  }

  return (
    <Box as='section' id='reviews' scrollMarginTop={`calc(${headerHeight} + 24px)`}>
      <Flex flexDirection='column' gap={4}>
        <ContentContainer>
          <Heading as='h2' size={{ base: 'heading-6', md: 'heading-5' }}>
            {getReviewsSectionTitle({ hasReviews, hasTestimonials })}
          </Heading>
        </ContentContainer>
        <GraduateTestimonialsSubsection programKey={programKey} />
        {hasReviews && (
          <>
            <TrustpilotReviewsSubsection
              numberOfReviews={numberOfReviews}
              reviews={carouselReviews}
              starsAverage={starsAverage}
              onArrowClick={handleReviewsArrowClick}
            />
            {carouselReviews.length > 0 && (
              <ContentContainer>
                <Flex alignItems='center' flexWrap='wrap' gap={4} justifyContent='space-between'>
                  <Text color='supportGray.70' fontSize='sm' lineHeight='sm'>
                    Showing {carouselReviews.length.toLocaleString('en-US')} out of{' '}
                    {formatCountLabel(numberOfReviews, 'Review')}
                  </Text>
                  <Button
                    rightIcon={<PlusIcon boxSize='20px' />}
                    size='md'
                    variant='outline'
                    onClick={handleReadMoreClick}
                  >
                    Read more reviews
                  </Button>
                </Flex>
              </ContentContainer>
            )}
          </>
        )}
      </Flex>
      {hasReviews && (
        <ReviewsModal
          isOpen={isModalOpen}
          numberOfReviews={numberOfReviews}
          programKey={programKey}
          starsAverage={starsAverage}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </Box>
  );
}
